import React, { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft, Bell, BellOff, Tag, Users, TrendingDown, Calendar,
  Check, ChevronRight, Trash2, Loader2, Lock, Crown
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import AlertTypeSettings from "../components/AlertTypeSettings";
import GuestAlertsPanel from "../components/GuestAlertsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_ICONS = {
  budget_deal: Tag,
  budget_reminder: Calendar,
  meal_suggestion: Calendar,
  meal_plan_ready: Calendar,
  deal_alert: Tag,
  community_deal: Users,
  price_drop: TrendingDown,
  weekly_summary: Calendar,
};

const NOTIF_COLORS = {
  deal_alert: "bg-green-100 text-green-600",
  community_deal: "bg-blue-100 text-blue-600",
  price_drop: "bg-amber-100 text-amber-600",
  weekly_summary: "bg-purple-100 text-purple-600",
};

const NotificationsPage = () => {
  const { user, token, hasPremiumAccess } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("notifications");
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscription, setPushSubscription] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user) return;
    setPushSupported("serviceWorker" in navigator && "PushManager" in window);
    checkPushSubscription();
    loadData();
  }, [token]); // eslint-disable-line

  const checkPushSubscription = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration("/sw-push.js");
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          setPushSubscription(sub);
        }
      }
    } catch (e) { /* ignore */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [notifRes, settingsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/push/notifications`, { headers }),
        fetch(`${BACKEND_URL}/api/push/settings`, { headers }),
      ]);
      if (notifRes.ok) {
        const d = await notifRes.json();
        setNotifications(d.notifications || []);
        setUnread(d.unread || 0);
      }
      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const updateSetting = async (key, value) => {
    const updated = key === "meal_slots"
      ? { ...settings, meal_slots: { ...(settings.meal_slots || {}), ...value } }
      : { ...settings, [key]: value };
    setSettings(updated);
    try {
      const res = await fetch(`${BACKEND_URL}/api/push/settings`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: value }),
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (e) { /* ignore */ }
  };

  const markRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
    try {
      await fetch(`${BACKEND_URL}/api/push/notifications/${id}/read`, {
        method: "PUT",
        headers,
      });
    } catch (e) { /* ignore */ }
  };

  const markAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    try {
      await fetch(`${BACKEND_URL}/api/push/notifications/read-all`, {
        method: "PUT",
        headers,
      });
    } catch (e) { /* ignore */ }
  };

  const togglePush = async () => {
    if (!pushSupported) return;
    setSubscribing(true);
    try {
      if (pushSubscription) {
        // Unsubscribe
        await pushSubscription.unsubscribe();
        await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user?.id, endpoint: pushSubscription.endpoint }),
        });
        setPushSubscription(null);
        updateSetting("push_enabled", false);
        toast({ title: "Push notifications disabled" });
      } else {
        // Subscribe — check permission first
        if ("Notification" in window && Notification.permission === "denied") {
          toast({ title: "Notifications Blocked", description: "You've previously blocked notifications. Go to your browser settings to allow notifications for this site.", variant: "destructive" });
          setSubscribing(false);
          return;
        }
        const reg = await navigator.serviceWorker.register("/sw-push.js");
        const vapidRes = await fetch(`${BACKEND_URL}/api/push/vapid-key`);
        const { public_key } = await vapidRes.json();
        if (!public_key) {
          toast({ title: "Push not available", description: "Server configuration missing", variant: "destructive" });
          setSubscribing(false);
          return;
        }
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key),
        });
        await fetch(`${BACKEND_URL}/api/push/subscribe`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user?.id, subscription: sub.toJSON() }),
        });
        setPushSubscription(sub);
        updateSetting("push_enabled", true);
        toast({ title: "Push notifications enabled!" });
      }
    } catch (e) {
      const msg = (e.message || "").toLowerCase();
      if (msg.includes("permission") || msg.includes("denied")) {
        toast({ title: "Permission Required", description: "Please allow notifications in your browser settings and try again.", variant: "destructive" });
      } else if (msg.includes("incognito") || msg.includes("private")) {
        toast({ title: "Not Supported", description: "Push notifications are not available in private/incognito mode. Please use a normal browser window.", variant: "destructive" });
      } else {
        toast({ title: "Registration failed", description: "Push notifications couldn't be enabled. Make sure you're not in incognito mode and notifications are allowed.", variant: "destructive" });
      }
    }
    setSubscribing(false);
  };

  const [testingSend, setTestingSend] = useState(false);

  const sendTestPush = async () => {
    setTestingSend(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/push/test`, {
        method: "POST",
        headers,
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: "Test sent!", description: "Check your phone — a push notification should arrive shortly." });
      } else {
        toast({ title: "Could not send", description: data.error || "Try enabling push notifications first.", variant: "destructive" });
      }
    } catch (e) {
      toast({ title: "Error", description: "Failed to send test notification.", variant: "destructive" });
    }
    setTestingSend(false);
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString();
  };

  if (!user) {
    return (
      <div className="p-4 space-y-4 pb-24" data-testid="notifications-page">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-full" data-testid="notif-back-btn">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-xl font-bold text-gray-900">Alerts</h2>
        </div>
        <GuestAlertsPanel />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-[50vh]">
        <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-full" data-testid="notif-back-btn">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
          <p className="text-sm text-gray-500">
            {unread > 0 ? `${unread} unread` : "All caught up"}
          </p>
        </div>
        {unread > 0 && (
          <Button size="sm" variant="outline" onClick={markAllRead} className="text-xs h-7" data-testid="mark-all-read-btn">
            <Check className="h-3 w-3 mr-1" /> Mark all read
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { key: "notifications", label: "Activity", count: unread },
          { key: "settings", label: "Settings" },
        ].map(t => (
          <button
            key={t.key}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors relative ${
              tab === t.key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => setTab(t.key)}
            data-testid={`notif-tab-${t.key}`}
          >
            {t.label}
            {t.count > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Activity Tab */}
      {tab === "notifications" && (
        <div className="space-y-2">
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400">No notifications yet</p>
              <p className="text-xs text-gray-300 mt-1">We'll alert you when items on your list go on sale</p>
            </div>
          ) : (
            notifications.map(n => {
              const Icon = NOTIF_ICONS[n.type] || Bell;
              const color = NOTIF_COLORS[n.type] || "bg-gray-100 text-gray-600";
              const isLocked = !hasPremiumAccess;
              return (
                <Card
                  key={n.id}
                  className={`cursor-pointer transition-colors ${!n.read ? "bg-green-50 border-green-200" : ""}`}
                  onClick={() => {
                    if (isLocked) {
                      navigate("/subscription");
                    } else if (!n.read) {
                      markRead(n.id);
                    }
                  }}
                  data-testid={`notification-${n.id}`}
                >
                  <CardContent className="p-3 flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium ${!n.read ? "text-gray-900" : "text-gray-600"}`}>
                          {n.title}
                        </p>
                        {!n.read && <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />}
                      </div>
                      {isLocked ? (
                        <>
                          <p className="text-xs text-gray-400 mt-0.5 blur-sm select-none" aria-hidden="true">
                            Great deals on your items at Kroger, Walmart, and more stores near you
                          </p>
                          <div className="flex items-center gap-2 mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="notif-upgrade-cta">
                            <Lock className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                            <p className="text-xs text-amber-700 font-medium">Subscribe to see deal details & save money</p>
                            <Crown className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 ml-auto" />
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                          {n.deals && n.deals.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {n.deals.slice(0, 3).map((d, i) => (
                                <Badge key={i} className="text-[10px] bg-green-100 text-green-700 font-normal">
                                  {d.item}: ${d.price?.toFixed(2)} @ {d.store}
                                </Badge>
                              ))}
                              {n.deals.length > 3 && (
                                <Badge className="text-[10px] bg-gray-100 text-gray-500 font-normal">
                                  +{n.deals.length - 3} more
                                </Badge>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1">{formatTime(n.created_at)}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Settings Tab */}
      {tab === "settings" && settings && (
        <div className="space-y-3">
          {/* Push Toggle */}
          <Card className="border-green-200 bg-green-50" data-testid="push-toggle-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                    {pushSubscription ? (
                      <Bell className="h-5 w-5 text-green-700" />
                    ) : (
                      <BellOff className="h-5 w-5 text-green-700" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Browser Push Notifications</p>
                    <p className="text-xs text-gray-500">
                      {pushSupported
                        ? pushSubscription ? "Enabled — you'll get alerts in your browser" : "Enable to get alerts even when the app is closed"
                        : "Not supported in this browser"}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className={`text-xs h-8 ${pushSubscription ? "bg-gray-200 text-gray-700 hover:bg-gray-300" : "bg-green-500 text-white hover:bg-green-600"}`}
                  onClick={togglePush}
                  disabled={!pushSupported || subscribing}
                  data-testid="push-toggle-btn"
                >
                  {subscribing ? <Loader2 className="h-3 w-3 animate-spin" /> : pushSubscription ? "Disable" : "Enable"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Push */}
          {pushSubscription && (
            <Button
              variant="outline"
              className="w-full text-sm border-green-300 text-green-700 hover:bg-green-50"
              onClick={sendTestPush}
              disabled={testingSend}
              data-testid="send-test-push-btn"
            >
              {testingSend ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bell className="h-4 w-4 mr-2" />}
              Send Test Notification
            </Button>
          )}

          {/* Alert Types */}
          <AlertTypeSettings settings={settings} onChange={updateSetting} />
        </div>
      )}
    </div>
  );
};

// Helper: Convert VAPID base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default NotificationsPage;
